import React, { useMemo } from 'react'
import { CODE_LOG_CONFIG, LANGUAGE_MAP } from '../utils/constants'
import { getLanguageInfo } from '../utils/fileHelpers'
import { highlight } from '../utils/codeHelpers'
import { primaryLanguageOf } from './CodeModal'

/* ───────────────────────────────────────────────────────────────
 *  CODE LOG CARD CONFIG  — SIZES live here, COLORS live per-language
 *  in constants.js → LANGUAGE_MAP (color / titleColor / captionColor).
 *
 *  The card = dark code preview on top + colored strip on the bottom.
 *  Strip background  = LANGUAGE_MAP[lang].color
 *  Title text color  = LANGUAGE_MAP[lang].titleColor
 *  Caption color     = LANGUAGE_MAP[lang].captionColor
 *
 *  To change the WIDTH / HEIGHT / number of preview lines, edit the
 *  values in CARD_CONFIG below (preview lines also read from
 *  CODE_LOG_CONFIG.feedPreviewLines in constants.js — change either).
 * ─────────────────────────────────────────────────────────────── */
const CARD_CONFIG = {
  // ── Overall card ──
  cardMaxWidth: '250px',          // ← WIDTH of the whole card
  cardRadius: '20px',
  cardMarginTop: '12px',
  cardBoxShadow: '0 1px 0 rgba(255,255,255,0.04) inset',

  // ── Preview area (top, dark code block) ──
  previewBg: '#101012',
  previewPaddingX: '16px',
  previewPaddingY: '14px',
  previewFontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "SF Pro Text", system-ui, sans-serif',
  previewFontSize: '12px',
  previewLineHeight: 1.55,
  previewMaxHeight: '470px',      // ← HEIGHT cap of the code preview block
  previewFadeHeight: '60px',      // bottom gradient fade
  previewLines: 17,               // ← how many lines of code to show

  // ── Strip (bottom, colored) ──
  stripPaddingX: '16px',
  stripPaddingY: '14px',
  stripTitleFontSize: '17px',
  stripTitleFontWeight: 700,
  stripTitleLetterSpacing: '-0.2px',
  stripCaptionFontSize: '12px',
  stripCaptionFontWeight: 500,
  stripGap: '3px',
}

/* Fallback colors if a language has no color set (or null language). */
const FALLBACK = { color: '#2C2C2E', titleColor: '#ffffff', captionColor: 'rgba(255,255,255,0.72)' }

function CodeLogCard({ log, onOpen }) {
  const files = log.code_files || []
  const title = log.code_title || 'Code'
  const fileCount = files.length

  // Strip + text colors come from the folder's primary language.
  const primaryLangId = useMemo(() => primaryLanguageOf(files), [files])
  const info = getLanguageInfo(primaryLangId) || FALLBACK
  const stripBg = info.color || FALLBACK.color
  const titleColor = info.titleColor || FALLBACK.titleColor
  const captionColor = info.captionColor || FALLBACK.captionColor

  // Preview: first file's first N lines, highlighted to its language.
  const previewFile = files[0]
  const previewLang = previewFile?.language || primaryLangId
  const previewHtml = useMemo(() => {
    if (!previewFile?.content) return ''
    const nLines = CARD_CONFIG.previewLines || CODE_LOG_CONFIG.feedPreviewLines || 15
    const lines = previewFile.content.split('\n').slice(0, nLines)
    return highlight(lines.join('\n'), previewLang)
  }, [previewFile, previewLang])

  return (
    <div
      onClick={onOpen}
      style={{
        maxWidth: CARD_CONFIG.cardMaxWidth,
        marginTop: CARD_CONFIG.cardMarginTop,
        borderRadius: CARD_CONFIG.cardRadius,
        overflow: 'hidden',
        boxShadow: CARD_CONFIG.cardBoxShadow,
        cursor: 'pointer',
      }}
    >
      {/* PREVIEW (dark code block, top) */}
      <div style={{
        position: 'relative',
        background: CARD_CONFIG.previewBg,
        padding: `${CARD_CONFIG.previewPaddingY} ${CARD_CONFIG.previewPaddingX}`,
        maxHeight: CARD_CONFIG.previewMaxHeight,
        overflow: 'hidden',
      }}>
        {previewHtml ? (
          <pre
            dangerouslySetInnerHTML={{ __html: previewHtml }}
            style={{
              margin: 0,
              fontFamily: CARD_CONFIG.previewFontFamily,
              fontSize: CARD_CONFIG.previewFontSize,
              fontWeight: 400,
              lineHeight: CARD_CONFIG.previewLineHeight,
              color: '#E8EAED',
              whiteSpace: 'pre',
              overflow: 'hidden',
            }}
          />
        ) : (
          <pre style={{
            margin: 0,
            fontFamily: CARD_CONFIG.previewFontFamily,
            fontSize: CARD_CONFIG.previewFontSize,
            lineHeight: CARD_CONFIG.previewLineHeight,
            color: 'rgba(255,255,255,0.35)',
            fontStyle: 'italic',
          }}>
            Empty file
          </pre>
        )}

        {/* Fade-out at the bottom */}
        <div style={{
          position: 'absolute',
          left: 0, right: 0, bottom: 0,
          height: CARD_CONFIG.previewFadeHeight,
          background: `linear-gradient(180deg, rgba(16,16,18,0) 0%, ${CARD_CONFIG.previewBg} 100%)`,
          pointerEvents: 'none',
        }} />
      </div>

      {/* STRIP (colored, bottom) */}
      <div style={{
        background: stripBg,
        padding: `${CARD_CONFIG.stripPaddingY} ${CARD_CONFIG.stripPaddingX}`,
        display: 'flex',
        flexDirection: 'column',
        gap: CARD_CONFIG.stripGap,
      }}>
        <div style={{
          color: titleColor,
          fontSize: CARD_CONFIG.stripTitleFontSize,
          fontWeight: CARD_CONFIG.stripTitleFontWeight,
          letterSpacing: CARD_CONFIG.stripTitleLetterSpacing,
          fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
        }}>
          {title}
        </div>
        <div style={{
          color: captionColor,
          fontSize: CARD_CONFIG.stripCaptionFontSize,
          fontWeight: CARD_CONFIG.stripCaptionFontWeight,
          fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
        }}>
          {fileCount} {fileCount === 1 ? 'File' : 'Files'}
        </div>
      </div>

      {/* Inject syntax token CSS (blue-only) */}
      <style>{`
        .syn-k { color:rgb(0, 123, 255); font-weight: 400; }
        .syn-s { color:rgb(44, 156, 255); font-weight: 400; }
        .syn-n { color:rgb(28, 85, 255); font-weight: 400; }
        .syn-c { color: #5C6B82; font-weight: 400; }
        .syn-p { color: #9AA6B2; font-weight: 400; }
      `}</style>
    </div>
  )
}

export default React.memo(CodeLogCard)